export type CognitoConfig = {
  baseUrl: string;
  redirectUrl: string;
  oauthProvider: string;
};

export type AppConfig = {
  API_URL: string;
  COGNITO_USER_POOL_ID: string;
  COGNITO_CLIENT_ID: string;
  X_API_KEY: string;
  INTERNAL_API_KEY: string;
  PUBLIC_API_URL: string;
  cognitoConfig: CognitoConfig;
  S3_ASSET_BUCKET: string;
  S3_FILE_BUCKET: string;
  REGION: string;
  IDENTITY_POOL_ID: string;
  PARTNER_ID: string;
  TEXT_EDITOR_KEY: string;
  SHOW_SSO_LOGIN: boolean;
  FIREBASE_CONFIG: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
  };
};

let cachedHeaders: { clientId: string; redirectUrl: string; showSSOLogin: boolean } | null = null;

async function loadConfigFromEnvironment(): Promise<{ clientId: string; redirectUrl: string; showSSOLogin: boolean }> {
  if (cachedHeaders) return cachedHeaders;

  const hostname = window.location.hostname;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1";

  let clientId = "";
  let redirectUrl = "";
  let showSSOLogin = false;

  if (isLocal) {
    clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
    redirectUrl = process.env.REACT_APP_COGNITO_REDIRECT_URL || "";
    showSSOLogin = process.env.REACT_APP_SHOW_SSO_LOGIN === "true";
  } else {
    const hostPrefix = hostname.split(".gimmeit.net.au")[0].toUpperCase();

    const clientIdEnvVar = `REACT_APP_${hostPrefix}_CLIENT_ID`;
    const redirectUrlEnvVar = `REACT_APP_${hostPrefix}_REDIRECT_URL`;
    const showSsoLoginEnvVar = `REACT_APP_${hostPrefix}_SHOW_SSO_LOGIN`;

    clientId = process.env[clientIdEnvVar] || "";
    redirectUrl = process.env[redirectUrlEnvVar] || "";
    showSSOLogin = process.env[showSsoLoginEnvVar] === "true" || false;
  }

  cachedHeaders = {
    clientId,
    redirectUrl,
    showSSOLogin
  };

  return cachedHeaders;
}

let config: AppConfig | null = null;

export async function loadConfig(): Promise<void> {
  const { clientId, redirectUrl, showSSOLogin } = await loadConfigFromEnvironment();

  config = {
    API_URL: process.env.REACT_APP_API_URL || "",
    COGNITO_USER_POOL_ID: process.env.REACT_APP_COGNITO_USER_POOL_ID || "",
    COGNITO_CLIENT_ID: clientId,
    X_API_KEY: process.env.REACT_APP_X_API_KEY || "",
    INTERNAL_API_KEY: process.env.REACT_APP_INTERNAL_API_KEY || "",
    PUBLIC_API_URL: process.env.REACT_APP_PUBLIC_API_URL || "",
    S3_ASSET_BUCKET: process.env.REACT_APP_S3_ASSET_BUCKET || "",
    S3_FILE_BUCKET: process.env.REACT_APP_S3_FILE_BUCKET || "",
    REGION: process.env.REACT_APP_REGION || "",
    IDENTITY_POOL_ID: process.env.REACT_APP_IDENTITY_POOL_ID || "",
    PARTNER_ID: process.env.REACT_APP_PARTNER_ID || "",
    TEXT_EDITOR_KEY: process.env.REACT_APP_TEXT_EDITOR_KEY || "",
    SHOW_SSO_LOGIN: showSSOLogin,
    FIREBASE_CONFIG: {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
      appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ""
    },
    cognitoConfig: {
      baseUrl: process.env.REACT_APP_COGNITO_BASE_URL || "",
      redirectUrl,
      oauthProvider:
        `${process.env.REACT_APP_COGNITO_BASE_URL}/oauth2/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodeURI(redirectUrl)}` ||
        ""
    }
  };
}

export function getConfig(): AppConfig {
  if (!config) throw new Error("Config not loaded yet");
  return config;
}
